/**
 * Blog Categories Management Page
 * Full CRUD implementation for blog categories
 */

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { adminApi } from "services/api";

// Below md the DataTable shows each row as a card, which would print long text in full; clamp it
// there (column `clampLines`, default 3) so cards stay scannable. Desktop cells are unchanged.
function ClampedCell({ value, column }) {
  return (
    <MDBox
      component="span"
      color="inherit"
      sx={({ breakpoints }) => ({
        [breakpoints.down("md")]: {
          display: "-webkit-box",
          WebkitLineClamp: column.clampLines || 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        },
      })}
    >
      {value}
    </MDBox>
  );
}

ClampedCell.propTypes = {
  value: PropTypes.node,
  column: PropTypes.shape({ clampLines: PropTypes.number }).isRequired,
};

// Bigger hit area for the row's "more" icon on touch screens (below md only).
const phoneTapTarget = ({ breakpoints }) => ({
  [breakpoints.down("md")]: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 40,
    minHeight: 40,
  },
});

function BlogCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", slug: "", description: "" });
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [menu, setMenu] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await adminApi.getBlogCategories();
      setCategories(response);
      setLoading(false);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to fetch categories");
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
    setFormData({ name: "", slug: "", description: "" });
    setError("");
  };

  const handleEdit = (category) => {
    setOpen(true);
    setEditMode(true);
    setCurrentId(category._id);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || "",
    });
    setError("");
    handleCloseMenu();
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ name: "", slug: "", description: "" });
    setEditMode(false);
    setCurrentId(null);
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Auto-generate slug from name
    if (name === "name" && !editMode) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData({ ...formData, name: value, slug });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.slug) {
      setError("Name and slug are required");
      return;
    }

    try {
      if (editMode) {
        await adminApi.updateBlogCategory(currentId, formData);
        setSuccess("Category updated successfully");
      } else {
        await adminApi.createBlogCategory(formData);
        setSuccess("Category created successfully");
      }
      handleClose();
      fetchCategories();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await adminApi.deleteBlogCategory(id);
        setSuccess("Category deleted successfully");
        fetchCategories();
        setTimeout(() => setSuccess(""), 3000);
      } catch (err) {
        setError(err.response?.data?.error || "Delete failed");
      }
    }
    handleCloseMenu();
  };

  const openMenu = (event, category) => {
    setMenu(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleCloseMenu = () => {
    setMenu(null);
    setSelectedCategory(null);
  };

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={Boolean(menu)}
      onClose={handleCloseMenu}
    >
      <MenuItem onClick={() => handleEdit(selectedCategory)}>Edit</MenuItem>
      <MenuItem onClick={() => handleDelete(selectedCategory._id)}>Delete</MenuItem>
    </Menu>
  );

  const columns = [
    { Header: "name", accessor: "name", width: "30%", Cell: ClampedCell, clampLines: 2 },
    { Header: "slug", accessor: "slug", width: "30%", Cell: ClampedCell, clampLines: 2 },
    { Header: "description", accessor: "description", width: "30%", Cell: ClampedCell },
    { Header: "actions", accessor: "actions", width: "10%", align: "center" },
  ];

  const rows = categories.map((category) => ({
    name: category.name,
    slug: category.slug,
    description: category.description || "-",
    actions: (
      <MDTypography
        component="a"
        href="#"
        variant="caption"
        color="secondary"
        fontWeight="medium"
        aria-label="Category actions"
        sx={phoneTapTarget}
        onClick={(e) => {
          e.preventDefault();
          openMenu(e, category);
        }}
      >
        <Icon>more_vert</Icon>
      </MDTypography>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={3}>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Card>
            <MDBox
              p={{ xs: 2, md: 3 }}
              display="flex"
              flexWrap="wrap"
              gap={1.5}
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">Blog Categories</MDTypography>
              <MDButton variant="gradient" color="info" onClick={handleOpen}>
                <Icon>add</Icon>&nbsp; Add Category
              </MDButton>
            </MDBox>
            <DataTable table={{ columns, rows }} canSearch />
          </Card>
        </MDBox>
      </MDBox>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editMode ? "Edit Category" : "Add New Category"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <MDBox mb={2}>
              <MDInput
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleClose} color="secondary">
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info">
              {editMode ? "Update" : "Create"}
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>

      {renderMenu}
    </DashboardLayout>
  );
}

export default BlogCategories;
